import React, { useEffect, useMemo, useState } from 'react';
import { NewsArticleJsonLd } from 'next-seo';
import { useRouter } from 'next/router';
import { useMediaQuery, useTheme } from '@mui/material';

// Components
import Markdown, { MarkdownWrapper } from '../../components/markdown/Markdown';

// Utils
import { correctHeightProportion } from '../../utils/image';
import { getStrapiMedia } from '../../utils/media';
import { getWorkshop } from '../../utils/api';
import { hexToRgb, rgbDataURL } from 'corporate-utils';
import { mapToNewsArticleJsonLd } from '../../utils/schemas';

// Models
import { Workshop } from '../../models/auto-generated-strapi-types/types/Workshop';
import { Slug } from '../../models/domain/interfaces';
import {
  HeaderCoverResizeType,
  HeaderDescriptionPositionType,
} from '../../models/enum';

// Style
import {
  Category,
  ContentDivision,
  CoverContainer,
  ImageContainerDefaultStyled,
  ImageContainerStyled,
  OverlayStyled,
  StyledContentWrapper,
  StyledVideo,
  Title,
  Wrapper,
} from './Workshop.style';
import { SingleContentWrapperMobile } from '../../styles/shared';
import { DEFAULT_IMAGE } from '../../models/domain/const';
import ImageWithFallback from '../../components/image-with-fallback/ImageWithFallback';

const WorkshopPage: React.FC<any> = ({
  data,
  translations,
  metadata,
  isPrerenderRequest,
}: {
  data: any;
  translations: any;
  metadata: any;
  isPrerenderRequest: boolean;
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpointsMediaQuery.up('sm'));
  const [, setRerender] = useState<number>(0); // Trick to force re-render

  const { locale } = useRouter();
  const workshop: Workshop = data?.workshop?.data?.[0] ?? {};

  const breadCrumb = {
    links: [
      {
        href: `/${locale}`,
        name: translations?.urls?.home,
      },
      {
        href: `/${translations?.urls?.workshops}`,
        name: translations?.urls?.workshops,
      },
    ],
    pageActive: {
      name:
        workshop?.attributes?.title ||
        workshop?.attributes?.category ||
        translations?.pages?.workshop?.category,
    },
    lightContrast: false,
  };
  const overlayColor = workshop?.attributes?.overlayColor;

  const image = useMemo(
    () =>
      typeof window === 'undefined' || isDesktop
        ? workshop?.attributes?.coverImage
        : workshop?.attributes?.mobileCoverImage?.data
          ? workshop?.attributes?.mobileCoverImage
          : workshop?.attributes?.coverImage,
    [
      isDesktop,
      workshop?.attributes?.coverImage,
      workshop?.attributes?.mobileCoverImage,
    ]
  );

  const video = useMemo(
    () =>
      typeof window === 'undefined' || isDesktop
        ? workshop?.attributes?.video
        : workshop?.attributes?.mobileVideo?.data
          ? workshop?.attributes?.mobileVideo
          : workshop?.attributes?.video,
    [isDesktop, workshop?.attributes?.mobileVideo, workshop?.attributes?.video]
  );

  useEffect(() => {
    setRerender(Date.now());
  }, [isDesktop, data, metadata]);

  const {
    titleFontSize,
    titleMobileFontSize,
    titleFontWeight,
    titleMobileFontWeight,
    forceDescriptionToBeBlack,
    forceDescriptionToBeWhite,
    forceHideDate,
    forceHideCategory,
    forceHideTitle,
    forceHideBreadcrumb,
    videoAutoPlay,
    videoControls,
    videoLoop,
    videoMuted,
  } = workshop?.attributes || {};

  const atLeastSomethingInDescriptionIsTrue = () => {
    return (
      !forceHideDate ||
      !forceHideCategory ||
      !forceHideTitle ||
      !forceHideBreadcrumb
    );
  };

  const categoryFieldData = !forceHideCategory
    ? [
      workshop?.attributes?.category ||
          translations?.pages?.workshop?.category,
    ]
    : [];
  if (!forceHideDate) {
    categoryFieldData.push(workshop?.attributes?.date);
  }

  const { headerDescriptionPosition, headerCoverResize } = workshop?.attributes;

  return (
    <StyledContentWrapper>
      {/* Image and description */}
      <CoverContainer headerCoverResize={headerCoverResize}>
        {/* COVER IMAGE */}
        {/* When image is cover */}
        <ImageContainerDefaultStyled headerCoverResize={headerCoverResize}>
          {image &&
            image.data &&
            image.data.attributes &&
            (HeaderCoverResizeType.DEFAULT_COVER ===
              (headerCoverResize as HeaderCoverResizeType) ||
              !headerCoverResize) && (
            <>
              {video?.data ? (
                <StyledVideo
                  autoPlay={videoAutoPlay || false}
                  controls={videoControls || false}
                  loop={videoLoop || false}
                  muted={videoMuted || false}
                  objectFit={'cover'}
                >
                  <source src={getStrapiMedia(video.data?.attributes?.url)} />
                </StyledVideo>
              ) : (
                <ImageWithFallback
                  isPrerenderRequest={isPrerenderRequest}
                  layout="fill"
                  objectFit="cover"
                  placeholder="blur"
                  blurDataURL={
                    overlayColor
                      ? rgbDataURL(
                        hexToRgb(overlayColor)?.[0],
                        hexToRgb(overlayColor)?.[1],
                        hexToRgb(overlayColor)?.[2]
                      )
                      : rgbDataURL(
                        hexToRgb('ffffff')?.[0],
                        hexToRgb('ffffff')?.[1],
                        hexToRgb('ffffff')?.[2]
                      )
                  }
                  src={getStrapiMedia(image?.data?.attributes?.url ?? '')}
                  alt={image?.data?.attributes?.alternativeText || ''}
                  fallbackSrc={DEFAULT_IMAGE}
                />
              )}
            </>
          )}
        </ImageContainerDefaultStyled>

        {/* COVER IMAGE */}
        {/* When image is contain */}
        <ImageContainerStyled>
          {image &&
            image?.data &&
            image?.data?.attributes &&
            (HeaderCoverResizeType.CONTAIN ===
              (headerCoverResize as HeaderCoverResizeType) ||
              !headerCoverResize) && (
            <>
              {video?.data ? (
                <StyledVideo
                  autoPlay={videoAutoPlay || false}
                  controls={videoControls || false}
                  loop={videoLoop || false}
                  muted={videoMuted || false}
                  objectFit={'contain'}
                >
                  <source src={getStrapiMedia(video.data?.attributes?.url)} />
                </StyledVideo>
              ) : (
                <ImageWithFallback
                  isPrerenderRequest={isPrerenderRequest}
                  width={'1920'}
                  height={
                    correctHeightProportion(
                      image?.data?.attributes?.height,
                      image?.data?.attributes?.width
                    ) || '600'
                  }
                  placeholder="blur"
                  blurDataURL={
                    overlayColor
                      ? rgbDataURL(
                        hexToRgb(overlayColor)?.[0],
                        hexToRgb(overlayColor)?.[1],
                        hexToRgb(overlayColor)?.[2]
                      )
                      : rgbDataURL(
                        hexToRgb('ffffff')?.[0],
                        hexToRgb('ffffff')?.[1],
                        hexToRgb('ffffff')?.[2]
                      )
                  }
                  src={getStrapiMedia(image?.data?.attributes?.url ?? '')}
                  alt={image?.data?.attributes?.alternativeText || ''}
                  fallbackSrc={DEFAULT_IMAGE}
                />
              )}
            </>
          )}
        </ImageContainerStyled>

        {/* Overlay cover */}
        {(typeof window === 'undefined' || isDesktop) && overlayColor ? (
          <OverlayStyled overlay={overlayColor}></OverlayStyled>
        ) : (
          <></>
        )}

        {/* Description inside image */}
        {HeaderDescriptionPositionType.DEFAULT ===
          (headerDescriptionPosition as HeaderDescriptionPositionType) &&
        atLeastSomethingInDescriptionIsTrue() ? (
            <Wrapper lightContrast={true && !forceDescriptionToBeBlack}>
              <ContentDivision>
                {!forceHideTitle && (
                  <Title
                    fontSize={titleFontSize}
                    fontMobileSize={titleMobileFontSize}
                    fontWeight={titleFontWeight}
                    fontMobileWeight={titleMobileFontWeight}
                  >
                    <Markdown>
                      {workshop?.attributes?.registration
                        ? workshop?.attributes?.titleRegistration
                        : workshop?.attributes?.title ?? ''}
                    </Markdown>
                  </Title>
                )}
                {categoryFieldData && (
                  <Category>
                    {categoryFieldData?.filter(Boolean).join(' | ')}
                  </Category>
                )}
              </ContentDivision>
            </Wrapper>
          ) : (
            <></>
          )}
      </CoverContainer>

      {/* Description under the image */}
      {HeaderDescriptionPositionType.UNDER ===
        (headerDescriptionPosition as HeaderDescriptionPositionType) &&
      atLeastSomethingInDescriptionIsTrue() ? (
          <MarkdownWrapper lightContrast={false || forceDescriptionToBeWhite}>
            <ContentDivision maxDimension={true}>
              {HeaderDescriptionPositionType.UNDER ===
            (headerDescriptionPosition as HeaderDescriptionPositionType) ? (
                  <>
                    {!forceHideTitle && (
                      <Title
                        fontSize={titleFontSize}
                        fontMobileSize={titleMobileFontSize}
                        fontWeight={titleFontWeight}
                        fontMobileWeight={titleMobileFontWeight}
                      >
                        <Markdown>
                          {workshop?.attributes?.registration
                            ? workshop?.attributes?.titleRegistration
                            : workshop?.attributes?.title ?? ''}
                        </Markdown>
                      </Title>
                    )}
                    {categoryFieldData && (
                      <Category>
                        {categoryFieldData?.filter(Boolean).join(' | ')}
                      </Category>
                    )}
                  </>
                ) : (
                  <></>
                )}
            </ContentDivision>
          </MarkdownWrapper>
        ) : (
          <></>
        )}

      {/* Text from markdown */}
      <SingleContentWrapperMobile>
        <MarkdownWrapper>
          <Markdown>
            {workshop?.attributes?.registration
              ? workshop?.attributes?.textRegistration
              : workshop?.attributes?.text ?? ''}
          </Markdown>
        </MarkdownWrapper>
      </SingleContentWrapperMobile>

      {/* SEO information */}
      <NewsArticleJsonLd
        {...mapToNewsArticleJsonLd(
          workshop,
          breadCrumb,
          metadata,
          translations,
          locale
        )}
      />
    </StyledContentWrapper>
  );
};

export const getServerSideProps = async (
  section: any,
  translations: any,
  slugAnalyzed: Slug
) => {
  const workshop =
    slugAnalyzed?.workshopSlug &&
    (await getWorkshop({
      slug: slugAnalyzed?.workshopSlug,
      locale: slugAnalyzed?.locale,
      dateFormat: translations?.date?.workshop?.format,
    }));

  section.workshop = {
    data: [
      {
        id: workshop?.id,
        attributes: {
          slug: workshop?.attributes?.slug,
          thumbnail: workshop?.attributes?.thumbnail,
          coverImage: workshop?.attributes?.coverImage,
          mobileThumbnail: workshop?.attributes?.mobileThumbnail,
          mobileCoverImage: workshop?.attributes?.mobileCoverImage,
          coverTextColor: workshop?.attributes?.coverTextColor,
          title: workshop?.attributes?.title,
          description: workshop?.attributes?.description,
          text: workshop?.attributes?.text,
          bottomImages: workshop?.attributes?.bottomImages || null,
          date: workshop?.attributes?.date,
          originalDate: workshop?.attributes?.originalDate,
          category: workshop?.attributes?.category,
          overlayColor: workshop?.attributes?.overlayColor,
          textRegistration: workshop?.attributes?.textRegistration,
          titleRegistration: workshop?.attributes?.titleRegistration,
          registration: workshop?.attributes?.registration,
          headerDescriptionPosition:
            workshop?.attributes?.headerDescriptionPosition ||
            HeaderDescriptionPositionType.DEFAULT,
          headerCoverResize:
            workshop?.attributes?.headerCoverResize ||
            HeaderCoverResizeType.DEFAULT_COVER,
          titleFontSize: workshop?.attributes?.titleFontSize,
          titleMobileFontSize: workshop?.attributes?.titleMobileFontSize,
          forceDescriptionToBeBlack:
            workshop?.attributes?.forceDescriptionToBeBlack,
          forceDescriptionToBeWhite:
            workshop?.attributes?.forceDescriptionToBeWhite,
          forceHideDate: workshop?.attributes?.forceHideDate,
          forceHideCategory: workshop?.attributes?.forceHideCategory,
          forceHideTitle: workshop?.attributes?.forceHideTitle,
          forceHideBreadcrumb: workshop?.attributes?.forceHideBreadcrumb,
          video: workshop?.attributes?.video,
          mobileVideo: workshop?.attributes?.mobileVideo,
          videoAutoPlay: workshop?.attributes?.videoAutoPlay,
          videoLoop: workshop?.attributes?.videoLoop,
          videoMuted: workshop?.attributes?.videoMuted,
          videoControls: workshop?.attributes?.videoControls,
        },
      },
    ],
  };
};

export default WorkshopPage;
